/**
 * Whether a CSS color is dark enough that the ink wash in `ImageSkeleton` would
 * vanish into it, meaning the white wash should be used instead.
 *
 * Handles the formats slide backgrounds actually come in: `#rgb`, `#rrggbb`,
 * `#rrggbbaa` and `rgb()` / `rgba()`. Anything it can't parse — gradients,
 * named colors, `transparent`, undefined — is treated as light, which matches
 * the beige default the app renders on.
 */
export const isDarkBackground = (color?: string | null): boolean => {
  const rgb = parseRgb(color);
  if (!rgb) return false;

  const [r, g, b] = rgb;
  // Perceived brightness (ITU-R BT.601). 0 = black, 255 = white.
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness < 140;
};

const parseRgb = (color?: string | null): [number, number, number] | undefined => {
  if (!color) return undefined;
  const value = color.trim().toLowerCase();

  if (value.startsWith('#')) {
    const hex = value.slice(1);
    if (hex.length === 3) {
      return [
        parseInt(hex[0] + hex[0], 16),
        parseInt(hex[1] + hex[1], 16),
        parseInt(hex[2] + hex[2], 16),
      ];
    }
    if (hex.length === 6 || hex.length === 8) {
      return [
        parseInt(hex.slice(0, 2), 16),
        parseInt(hex.slice(2, 4), 16),
        parseInt(hex.slice(4, 6), 16),
      ];
    }
    return undefined;
  }

  const match = value.match(
    /^rgba?\(\s*(\d+)\s*[,\s]\s*(\d+)\s*[,\s]\s*(\d+)\s*(?:[,/].*)?\)$/,
  );
  if (!match) return undefined;
  return [Number(match[1]), Number(match[2]), Number(match[3])];
};
